import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PhygitalIcon from "@/components/PhygitalIcon";

const TermsPage = () => {
  const lastUpdated = "March 30, 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <div className="flex items-center gap-2">
              <PhygitalIcon className="w-6 h-6" />
              <span className="font-semibold">phygital</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: {lastUpdated}</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using Phygital ("Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              Phygital is a preference-sharing platform that enables:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Event Attendees:</strong> Share accessibility needs, dietary preferences, and interests with event hosts</li>
              <li><strong>Event Hosts:</strong> Collect and manage guest preferences for better event experiences</li>
              <li><strong>Beauty Clients:</strong> Share style preferences and inspiration with service providers</li>
              <li><strong>Service Providers:</strong> Access client preferences, manage client profiles, and curate inspo galleries for better service</li>
              <li><strong>Personal Profiles:</strong> Save defaults, collect inspo, plan experiences, and share with providers across 11 categories</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
            <p className="text-gray-700 mb-4">
              You may create an account using your phone number or Google account. You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Maintaining the confidentiality of your account</li>
              <li>All activities that occur under your account</li>
              <li>Providing accurate and complete information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. User Content & Preferences</h2>
            <p className="text-gray-700 mb-4">
              You retain ownership of any preferences, photos, or content you submit. By sharing preferences, you grant:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Event hosts permission to view your event-related preferences for events you RSVP to</li>
              <li>Service providers permission to view your beauty/style preferences when you share your profile</li>
              <li>Phygital a license to store and display your content as needed to provide the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Event Host Responsibilities</h2>
            <p className="text-gray-700 mb-4">
              Event hosts using Phygital agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Use guest preference data only for improving the event experience</li>
              <li>Not share guest data with third parties without consent</li>
              <li>Handle accessibility and dietary information responsibly</li>
              <li>Comply with all applicable laws regarding data collection</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Service Provider Responsibilities</h2>
            <p className="text-gray-700 mb-4">
              Beauty and service providers using Phygital agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Use client preferences only to improve service delivery</li>
              <li>Maintain client confidentiality</li>
              <li>Not share client data without explicit consent</li>
              <li>Provide services in accordance with professional standards</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Prohibited Uses</h2>
            <p className="text-gray-700 mb-4">
              You may not use Phygital to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Violate any laws or regulations</li>
              <li>Harass, discriminate against, or harm other users</li>
              <li>Collect user data for purposes outside the Service</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Transmit malware or other harmful code</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Digital Passes & Event Access</h2>
            <p className="text-gray-700 mb-4">
              Digital passes generated through Phygital are for personal use only. Passes may be revoked if:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>The event is cancelled or rescheduled</li>
              <li>The pass holder violates event policies</li>
              <li>The pass is transferred without authorization</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Subscription & Payments</h2>
            <p className="text-gray-700 mb-4">
              For paid tiers (Solo, Team, phygitalLuxe):
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Subscriptions are billed monthly</li>
              <li>You may cancel at any time; access continues until the end of the billing period</li>
              <li>Refunds are provided at our discretion</li>
              <li>Prices may change with 30 days notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              Phygital is provided "as is" without warranties of any kind. We are not liable for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Service interruptions or data loss</li>
              <li>Actions of event hosts, service providers, or other users</li>
              <li>Event cancellations or service quality issues</li>
              <li>Indirect, incidental, or consequential damages</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We may update these Terms at any time. Continued use of the Service after changes constitutes 
              acceptance of the new Terms. We will notify users of material changes via email or in-app notification.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms, contact us at:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> <a href="mailto:hello@phygital.systems" className="text-purple-600 hover:underline">hello@phygital.systems</a><br />
              <strong>Website:</strong> phygital.systems
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
